import {
  ActionIcon,
  Anchor,
  Button,
  CopyButton,
  Group,
  List,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconCopy, IconExternalLink } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import { getChannelDeeplinkUrl } from '@/utils/sponsors'

interface WebSettingsPanelProps {
  registerUnsavedChangesChecker: (tabKey: 'webSettings', checker: () => boolean, onDiscard: () => void) => void
}

const IMPLEMENTATION_KIT_URL = 'https://sidekickplatform.com/resources/implementation-kit/'

function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function WebSettingsPanel({ registerUnsavedChangesChecker }: WebSettingsPanelProps) {
  const { api, authenticationStore, configStore } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const [isPending, setIsPending] = useState(false)

  const channelUrl = getChannelDeeplinkUrl(configStore.authBaseUrl, authenticationStore.user?.sponsorId)

  const sponsorInfoQuery = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const [originalUrl, setOriginalUrl] = useState<string>('')
  const [subscriptionUrl, setSubscriptionUrl] = useState<string>('')
  const hasEditsRef = useRef(false)

  useEffect(() => {
    if (sponsorInfoQuery.data) {
      const url = sponsorInfoQuery.data.subscriptionUrl ?? ''
      setOriginalUrl(url)
      if (!hasEditsRef.current) {
        setSubscriptionUrl(url)
      }
    }
  }, [sponsorInfoQuery.data])

  const hasChanges = useMemo(() => {
    return originalUrl !== subscriptionUrl
  }, [originalUrl, subscriptionUrl])

  useEffect(() => {
    hasEditsRef.current = hasChanges
  }, [hasChanges])

  const handleCancel = useCallback(() => {
    setSubscriptionUrl(originalUrl)
  }, [originalUrl])

  useEffect(() => {
    registerUnsavedChangesChecker('webSettings', () => hasChanges, handleCancel)
  }, [registerUnsavedChangesChecker, hasChanges, handleCancel])

  const handleSave = useCallback(async () => {
    const trimmed = subscriptionUrl.trim()
    if (trimmed && !isValidUrl(trimmed)) {
      notifications.show({
        title: 'Invalid URL',
        message: 'Please enter a valid URL (e.g. https://example.com).',
        color: 'red',
      })
      return
    }

    setIsPending(true)
    try {
      await api.updateSubscriptionUrl(trimmed || null)
      setOriginalUrl(trimmed)
      setSubscriptionUrl(trimmed)
      await api.queryClient.invalidateQueries({ queryKey: ['sponsor', 'info'] })
      notifications.show({
        title: 'Settings Saved',
        message: 'Your web settings have been updated.',
        color: 'green',
      })
    } catch (error) {
      console.error('Error saving subscription URL:', error)
      notifications.show({
        title: 'Save Failed',
        message: 'Could not save web settings. Please try again.',
        color: 'red',
      })
    } finally {
      setIsPending(false)
    }
  }, [api, subscriptionUrl])

  if (sponsorInfoQuery.isLoading || sponsorInfoQuery.isError) {
    return (
      <Stack>
        <Paper radius="md" p="lg" shadow="sm" px="xl">
          <Group justify="space-between" align="center" p="md">
            <Title order={4}>Web Settings</Title>
            {sponsorInfoQuery.isLoading && <Text>Loading...</Text>}
            {sponsorInfoQuery.isError && <Text>Error loading data</Text>}
          </Group>
        </Paper>
      </Stack>
    )
  }

  return (
    <Stack>
      <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
        <Group justify="space-between" mb="md">
          <Title order={isMobile ? 6 : 4}>Connect Your Sidekick Channel to Your Website</Title>
          <Group>
            <Button variant="outline" onClick={handleCancel} disabled={!hasChanges || isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges || isPending} loading={isPending}>
              Save
            </Button>
          </Group>
        </Group>

        <Text size="md" mb="lg">
          Make it easy for users to access your Sidekick channel from your website.
        </Text>

        <List type="ordered" spacing="xl">
          <List.Item>
            <Text fw={600} mb="xs">
              Add the channel link to your website
            </Text>
            <Text size="sm" mb="sm">
              Add this link to a button, menu item, or call-to-action anywhere on your site. It will guide users
              directly to your channel on the Sidekick Platform.
            </Text>
            {channelUrl && (
              <Group gap="xs" maw={500}>
                <TextInput
                  value={channelUrl}
                  readOnly
                  flex={1}
                  size="sm"
                  styles={{ input: { fontFamily: 'monospace' } }}
                />
                <CopyButton value={channelUrl} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                      <ActionIcon
                        color={copied ? 'teal' : 'gray'}
                        variant="subtle"
                        onClick={copy}
                        size="lg"
                        aria-label={copied ? 'Channel URL copied' : 'Copy channel URL'}>
                        {copied ? (
                          <IconCheck size={18} aria-hidden="true" />
                        ) : (
                          <IconCopy size={18} aria-hidden="true" />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
                <Tooltip label="Open in new tab" withArrow>
                  <ActionIcon
                    component="a"
                    href={channelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="gray"
                    variant="subtle"
                    size="lg"
                    aria-label="Open channel URL in new tab">
                    <IconExternalLink size={18} aria-hidden="true" />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}
          </List.Item>

          <List.Item>
            <Text fw={600} mb="xs">
              Build your website button
            </Text>
            <Text size="sm">
              For button designs, assets, and step-by-step setup instructions, visit the{' '}
              <Anchor href={IMPLEMENTATION_KIT_URL} target="_blank" rel="noopener noreferrer">
                Sidekick Implementation Kit
              </Anchor>
              .
            </Text>
          </List.Item>

          <List.Item>
            <Text fw={600} mb="xs">
              Add a request access link (Optional)
            </Text>
            <Text size="sm" mb="sm">
              Provide a link where users can request access to your channel. This can be a subscription management
              system or a simple form or signup page that you manage.
            </Text>
            <TextInput
              label="Add your link here:"
              value={subscriptionUrl}
              onChange={e => setSubscriptionUrl(e.target.value)}
              placeholder="https://..."
              disabled={isPending}
              size="sm"
              maw={500}
              mb="sm"
            />
            <Text size="sm" c="dimmed">
              After users submit the form or subscribe, you must invite them through the{' '}
              <Text span fw={700}>
                People
              </Text>{' '}
              page in this Sidekick Portal.
            </Text>
          </List.Item>
        </List>
      </Paper>
    </Stack>
  )
}
